import React, { useMemo, useState } from 'react';
import Tooltip from "@mui/material/Tooltip";
import { theme } from "src/theme/mui-theme";
import { DeviceCategory, DeviceItem, DeviceSubcategory, } from "src/model/api/v2/data-contracts";
import { MainAndSubItemIndex, NestedAccordionView } from "src/components/NestedAccordionView/NestedAccordionView";
import { SubcategoryIcon } from "src/icons/planner/subcategoryIcons/SubcategoryIcon";
import { NotVisibleIcon } from "src/icons/planner/subcategoryIcons/deviceIcons/NotVisibleIcon";
import { VisibleIcon } from "src/icons/planner/subcategoryIcons/deviceIcons/VisibleIcon";
import { LockedIcon } from "src/icons/planner/subcategoryIcons/deviceIcons/LockedIcon";
import { UnlockedIcon } from "src/icons/planner/subcategoryIcons/deviceIcons/UnlockedIcon";
import { calcImageURL } from "src/utils/url";
import { ts } from "src/utils/translate";
import { mapDeviceItemToDraggable } from "src/pages/PlannerPage/DeviceView/hooks/DragAndDrop/draggableDevices";
import { DraggableListItem } from "src/pages/PlannerPage/DeviceView/hooks/DragAndDrop/DraggableListItem";
import { SelectedDevice } from "src/api/planner-ui-slice";
import { executeToggleLock, executeToggleVisibility, } from "src/pages/PlannerPage/DeviceView/categoryToggleActions";
import { toggleIn } from "src/components/Planner/planner/categoryDisplayHandler";

export interface DeviceAccordionViewProps {
    categories: DeviceCategory[];
    selectedDevice: SelectedDevice;
    autoExpandAll?: boolean;
}

interface DeviceCategoryMapEntry extends Required<MainAndSubItemIndex> {
    subcategoryId: number;
}

export function DeviceAccordionView({
    categories,
    selectedDevice,
    autoExpandAll,
}: DeviceAccordionViewProps) {
    const selectedDeviceProductNameId = selectedDevice.productNameId;

    const [hiddenSubcategoryIds, setHiddenSubcategoryIds] = useState<number[]>([]);
    const [lockedSubcategoryIds, setLockedSubcategoryIds] = useState<number[]>([]);

    const handleToggleVisibility = (subcategory: DeviceSubcategory) => {
        executeToggleVisibility(subcategory.id);
        setHiddenSubcategoryIds(prev => toggleIn(prev, subcategory.id));
    };

    const handleToggleLock = (subcategory: DeviceSubcategory) => {
        executeToggleLock(subcategory.id);
        setLockedSubcategoryIds(prev => toggleIn(prev, subcategory.id));
    };

    const deviceCategoryMap = useMemo(() => {
        const map = new Map<string, DeviceCategoryMapEntry>();
        categories.forEach((category, mainIndex) => {
            category.subcategories.forEach((subcategory, subIndex) => {
                subcategory.devices.forEach((device) => {
                    map.set(device.id, { mainIndex, subIndex, subcategoryId: subcategory.id });
                });
            });
        });
        return map;
    }, [categories]);

    const selectedMainAndSubItem = selectedDeviceProductNameId != null
        ? deviceCategoryMap.get(selectedDeviceProductNameId) ?? null
        : null;

    return (
        <NestedAccordionView
            data={categories}
            getMainLabel={(category: DeviceCategory) => category.name}
            getSubList={(category: DeviceCategory) => category.subcategories}
            getSubLabel={(subcategory: DeviceSubcategory) => subcategory.name}
            getItems={(subcategory: DeviceSubcategory) => subcategory.devices}
            getItemKey={(device: DeviceItem) => device.id}
            getItemLabel={(device: DeviceItem) => device.text}

            renderSubIcon={(subcategory, isExpanded) => (
                <SubcategoryIcon
                    id={subcategory.icon ?? ''}
                    sx={{ color: isExpanded ? theme.palette.primary.main : theme.palette.text.primary }}
                />
            )}
            renderSubActions={(subcategory, isExpanded) => {
                const isHidden = hiddenSubcategoryIds.includes(subcategory.id);
                const isLocked = lockedSubcategoryIds.includes(subcategory.id);
                return (
                    <>
                        <Tooltip title={
                            isHidden
                                ? ts('unhide_devices_tooltip', 'Unhide devices on the floor')
                                : ts('hide_devices_tooltip', 'Hide devices on the floor')
                        }>
                            <span onClick={(event) => {
                                event.stopPropagation();
                                handleToggleVisibility(subcategory);
                            }}>
                                {isHidden ? (
                                    <NotVisibleIcon
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            color: isExpanded
                                                ? theme.palette.primary.main
                                                : theme.palette.text.primary,
                                        }}
                                    />
                                ) : (
                                    <VisibleIcon
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            color: isExpanded
                                                ? theme.palette.primary.main
                                                : theme.palette.text.primary,
                                        }}
                                    />
                                )}
                            </span>
                        </Tooltip>

                        <Tooltip title={
                            isLocked
                                ? ts('unlock_devices_tooltip', 'Unlock devices on the floor')
                                : ts('lock_devices_tooltip', 'Lock devices on the floor')
                        }>
                            <span onClick={(event) => {
                                event.stopPropagation();
                                handleToggleLock(subcategory);
                            }}>
                                {isLocked ? (
                                    <LockedIcon
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            color: isExpanded
                                                ? theme.palette.primary.main
                                                : theme.palette.text.primary,
                                        }}
                                    />
                                ) : (
                                    <UnlockedIcon
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            color: isExpanded
                                                ? theme.palette.primary.main
                                                : theme.palette.text.primary,
                                        }}
                                    />
                                )}
                            </span>
                        </Tooltip>
                    </>
                );
            }}

            renderItem={(device: DeviceItem, ref, selected) => (
                <DraggableListItem
                    ref={ref}
                    dragData={mapDeviceItemToDraggable(device, deviceCategoryMap.get(device.id)?.subcategoryId ?? 0)}
                    icon={device.icon ? <img src={calcImageURL(device.icon)} alt={device.text} width={24} height={24} /> : undefined}
                    label={device.text}
                    selected={selected}
                />
            )}

            mainLabelColor={theme.palette.primary.main}
            mainExpandIconColor={theme.palette.primary.main}
            expandedMainAndSubItem={selectedMainAndSubItem}
            expansionTrigger={selectedDevice}
            selectedItemKey={selectedDeviceProductNameId}
            autoExpandAll={autoExpandAll}
        />
    );
}